import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Store as StoreIcon,
} from '@mui/icons-material';

interface PortalCompanyInfoProps {
  customer: any;
  welcomeMessage?: string;
}

export const PortalCompanyInfo: React.FC<PortalCompanyInfoProps> = ({ customer, welcomeMessage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Separate service and billing locations
  const serviceLocations = customer.locations?.filter((loc: any) => loc.type === 'Service') || [];
  const billingLocations = customer.locations?.filter((loc: any) => loc.type === 'Billing') || [];

  return (
    <Grid container spacing={isMobile ? 2 : 3}>
      {/* Welcome Message */}
      {welcomeMessage && (
        <Grid item xs={12}>
          <Paper sx={{ p: isMobile ? 2 : 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
            <Typography variant={isMobile ? 'body2' : 'body1'} color="text.primary">
              {welcomeMessage}
            </Typography>
          </Paper>
        </Grid>
      )}

      {/* Company & Contact Information */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon color="primary" sx={{ mr: 1, fontSize: isMobile ? 20 : 24 }} />
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="primary" sx={{ fontWeight: 600 }}>
                Company Information
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              {customer.company && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Company Name
                  </Typography>
                  <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={500}>
                    {customer.company}
                  </Typography>
                </Box>
              )}

              {(customer.firstName || customer.lastName) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Primary Contact
                  </Typography>
                  <Typography variant={isMobile ? 'body2' : 'body1'}>
                    {`${customer.firstName || ''} ${customer.lastName || ''}`.trim()}
                    {customer.title && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {customer.title}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}

              {customer.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ mr: 1, fontSize: isMobile ? 18 : 20, color: 'text.secondary' }} />
                  <Typography variant={isMobile ? 'body2' : 'body1'}>{customer.phone}</Typography>
                </Box>
              )}

              {customer.email && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 1, fontSize: isMobile ? 18 : 20, color: 'text.secondary' }} />
                  <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ wordBreak: 'break-word' }}>
                    {customer.email}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Service Locations */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationIcon color="primary" sx={{ mr: 1, fontSize: isMobile ? 20 : 24 }} />
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="primary" sx={{ fontWeight: 600 }}>
                Service Locations
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {serviceLocations.length > 0 ? (
              <List sx={{ py: 0 }}>
                {serviceLocations.map((loc: any, index: number) => (
                  <ListItem
                    key={loc.location.id}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: index < serviceLocations.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <StoreIcon color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={500}>
                          {loc.location.name || 'Service Location'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {[
                            loc.location.streetLine1,
                            loc.location.streetLine2,
                            loc.location.city && loc.location.province && loc.location.postalCode
                              ? `${loc.location.city}, ${loc.location.province} ${loc.location.postalCode}`
                              : [loc.location.city, loc.location.province, loc.location.postalCode].filter(Boolean).join(', ')
                          ].filter(Boolean).join(', ') || 'No address available'}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No service locations configured
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Billing Locations (if different from service) */}
      {billingLocations.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HomeIcon color="primary" sx={{ mr: 1, fontSize: isMobile ? 20 : 24 }} />
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="primary" sx={{ fontWeight: 600 }}>
                  Billing Address
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {billingLocations.map((loc: any) => (
                <Box key={loc.location.id}>
                  <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={500}>
                    {loc.location.name || 'Billing Address'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {loc.location.streetLine1}
                    {loc.location.streetLine2 && (
                      <><br />{loc.location.streetLine2}</>
                    )}
                    {loc.location.city && (
                      <>
                        <br />
                        {[loc.location.city, loc.location.province, loc.location.postalCode].filter(Boolean).join(', ')}
                      </>
                    )}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};